import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import WizardCst from "@components/WizardCst";
import {
  ValidationResult,
  WizardHandle,
  WizardStep,
  WizardStepProps,
} from "@components/WizardCst/types";
import { useNavbarInitiative } from "@hooks/useNavbarInitiative";
import { LoTranslationProvider } from "../hooks/useLoTranslation";
import { ActivityWizardData, AssociationItem } from "../AddActivity/types";
import TypeSelectionStep from "../AddActivity/steps/TypeSelectionStep";
import MainInfoStep from "../AddActivity/steps/MainInfoStep";
import { validateMainInfo } from "../AddActivity/steps/MainInfoStep/validation/validateMainInfo";
import AdditionalInfoStep from "../AddActivity/steps/AdditionalInfoStep";
import { validateAdditionalInfo } from "../AddActivity/steps/AdditionalInfoStep/validation/validateAdditionalInfo";
import AssociateInitiativeStep from "../AddActivity/steps/AssociateInitiativeStep";
import { validateAssociateInitiative } from "../AddActivity/steps/AssociateInitiativeStep/validation/validateAssociateInitiative";
import EditorialStep from "../AddActivity/steps/EditorialStep";
import { validateEditorial } from "../AddActivity/steps/EditorialStep/validation/validateEditorial";

export interface ActivityWizardProps {
  initialData: ActivityWizardData;
  onComplete: (data: ActivityWizardData) => void | Promise<void>;
  onSaveDraft?: (data: ActivityWizardData) => void | Promise<void>;
  isSavingDraft?: boolean;
  isSubmitting?: boolean;
  mode?: "add" | "edit";
}

const ActivityWizard: React.FC<ActivityWizardProps> = ({
  initialData,
  onComplete,
  onSaveDraft,
  isSavingDraft,
  isSubmitting,
  mode = "add",
}) => {
  const { t } = useTranslation("wizard-activity");
  const navigate = useNavigate();
  const { id: corporateId = "", idLO } = useParams();

  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;
  const isEditMode = mode === "edit";

  const wizardRef = useRef<WizardHandle<ActivityWizardData>>(null);
  const { selectedInitiative, selectedInitiativeName } = useNavbarInitiative();
  const prevNavbarRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const next = selectedInitiative;
    const prev = prevNavbarRef.current;
    if (next === prev) return;
    prevNavbarRef.current = next;

    wizardRef.current?.setData((current) => {
      let list = current.selected_initiatives;

      if (prev && prev !== "all") {
        const oldId = Number(prev);
        if (!Number.isNaN(oldId)) {
          list = list.filter((it) => Number(it.id) !== oldId);
        }
      }

      if (next && next !== "all") {
        const newId = Number(next);
        if (
          !Number.isNaN(newId) &&
          !list.some((it) => Number(it.id) === newId)
        ) {
          const item: AssociationItem = {
            id: newId,
            label: selectedInitiativeName,
          };
          list = [...list, item];
        }
      }
      return list === current.selected_initiatives
        ? {}
        : { selected_initiatives: list };
    });
  }, [selectedInitiative, selectedInitiativeName]);

  const isPublishedInitial = useMemo(() => {
    if (!isEditMode) return false;
    const pd = initialData.publish_date;
    if (!pd) return false;
    const ts = new Date(pd).getTime();
    if (Number.isNaN(ts)) return false;
    return Date.now() > ts;
  }, [isEditMode, initialData.publish_date]);

  const lockPublication = isPublishedInitial;

  const MainInfoStepBound = useMemo(() => {
    const Bound: React.FC<WizardStepProps<ActivityWizardData>> = (props) => (
      <MainInfoStep {...props} lockPublication={lockPublication} />
    );
    return Bound;
  }, [lockPublication]);

  const steps = useMemo<WizardStep<ActivityWizardData>[]>(() => {
    const all: WizardStep<ActivityWizardData>[] = [
      {
        key: "type-selection",
        label: t("steps.type-selection.label"),
        Component: TypeSelectionStep,
        validate: (data): ValidationResult => ({
          valid: !!data.type,
          errors: data.type ? {} : { type: "required" },
        }),
      },
      {
        key: "main-info",
        label: t("steps.main-info.label"),
        Component: MainInfoStepBound,
        validate: validateMainInfo,
      },
      {
        key: "additional-info",
        label: t("steps.additional-info.label"),
        Component: AdditionalInfoStep,
        validate: validateAdditionalInfo,
      },
      {
        key: "associate-initiative",
        label: t("steps.associate-initiative.label"),
        Component: AssociateInitiativeStep,
        validate: validateAssociateInitiative,
      },
      {
        key: "editorial",
        label: t("steps.editorial.label"),
        Component: EditorialStep,
        validate: validateEditorial,
      },
    ];
    return isEditMode ? all.filter((s) => s.key !== "type-selection") : all;
  }, [t, MainInfoStepBound, isEditMode]);

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3 }}>
        <LoTranslationProvider loId={isEditMode ? idLO : undefined}>
          <WizardCst<ActivityWizardData>
            ref={wizardRef}
            title={isEditMode ? t("title-edit") || t("title") : t("title")}
            breadcrumb={<Breadcrumb />}
            backLink={{
              label: t("back-link"),
              to: catalogUrl,
            }}
            steps={steps}
            initialData={initialData}
            onComplete={onComplete}
            onCancel={() => navigate(catalogUrl)}
            abandonMode={isEditMode ? "edit" : "create"}
            resetConfirm={{
              title: t("reset-confirm.title"),
              description: t("reset-confirm.description"),
              confirmLabel: t("reset-confirm.confirm"),
              cancelLabel: t("reset-confirm.cancel"),
            }}
            nextLabel={t("next") || "Prosegui"}
            resetLabel={t("reset") || "Reset"}
            completeLabel={t("publish") || "Pubblica"}
            backLabel={t("back") || "Indietro"}
            secondaryAction={
              onSaveDraft
                ? {
                    label: t("save-draft") || "Salva bozza",
                    onClick: onSaveDraft,
                    disabled: !!isSavingDraft,
                  }
                : undefined
            }
            isCompleting={isSubmitting}
          />
        </LoTranslationProvider>
      </Box>
    </AnimationFadeIn>
  );
};

export default ActivityWizard;
